import type { FC, ReactNode } from 'react'
import type { ModuleId } from '@/types'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import { cn } from '@/lib/utils'
import { BrandLockup } from '@/components/generals/logo'

import {
    BadgeCheck,
    BatteryFull,
    BookOpen,
    Briefcase,
    Check,
    Clock,
    Download,
    FileText,
    Flame,
    House,
    MapPin,
    QrCode,
    Route,
    Signal,
    Store,
    Wifi
} from 'lucide-react'

interface PhoneScreenProps {
    active: ModuleId
    onSelect: (id: ModuleId) => void
}

const TABS: Array<{ id: ModuleId, label: string, icon: typeof House }> = [
    { id: 'student-hub', label: "Home", icon: House },
    { id: 'journey', label: "Journey", icon: Route },
    { id: 'study-vault', label: "Vault", icon: BookOpen },
    { id: 'placements', label: "Jobs", icon: Briefcase },
    { id: 'marketplace', label: "Clubs", icon: Store }
]

const TITLES: Record<ModuleId, string> = {
    'student-hub': "My Pass",
    journey: "This Week",
    'study-vault': "Study Vault",
    placements: "Applications",
    marketplace: "Event Check-in"
}

const PassScreen: FC = (): ReactNode => (
    <div className='space-y-4'>
        <div className='relative overflow-hidden rounded-[28px] bg-[linear-gradient(150deg,#312E81,#1E1B4B_45%,#0B1628)] p-5 text-white shadow-[0_20px_40px_-18px_rgba(49,46,129,0.8)]'>
            <span className='absolute -right-16 -top-16 size-44 rounded-full bg-indigo-400/20 blur-2xl' />
            <span className='absolute -bottom-20 -left-10 size-48 rounded-full bg-emerald-400/15 blur-2xl' />

            <div className='relative flex items-center justify-between'>
                <BrandLockup size={34} subtitle='Student Digital Pass' />

                <span className='flex items-center gap-1 rounded-full bg-emerald-400/20 px-2 py-1 text-[10px] font-bold text-emerald-200 ring-1 ring-emerald-300/30'>
                    <BadgeCheck className='size-3' />
                    Verified
                </span>
            </div>

            <div className='relative mt-5 flex items-center gap-3'>
                <span className='grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-amber-300 to-rose-400 text-[18px] font-extrabold text-slate-900'>
                    AS
                </span>

                <div>
                    <div className='text-[19px] font-bold leading-tight'>Aarav Sharma</div>
                    <div className='text-[12px] text-indigo-200'>B.Tech · Computer Science</div>
                    <div className='mt-0.5 font-mono text-[11px] text-slate-300'>Year 2 · 23FET1042</div>
                </div>
            </div>

            <div className='relative mt-5 flex items-center gap-4 rounded-2xl bg-white p-3'>
                <QRCodeSVG value='GKV-7KQ2-M9XD' size={104} level='M' bgColor='#FFFFFF' fgColor='#0B1628' />

                <div className='text-slate-900'>
                    <div className='text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400'>Pass code</div>
                    <div className='font-mono text-[14px] font-bold'>GKV-7KQ2-M9XD</div>
                    <div className='mt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400'>Plan</div>
                    <div className='text-[12px] font-semibold'>4-Year Complete</div>
                </div>
            </div>

            <div className='relative mt-4 flex justify-between text-[11px] text-indigo-200'>
                <span>Valid through Year 4</span>
                <span>Scan to verify</span>
            </div>
        </div>

        <div className='grid grid-cols-3 gap-2.5'>
            {[
                { label: "Check-in", icon: QrCode, tone: 'bg-indigo-50 text-indigo-600' },
                { label: "PYQs", icon: BookOpen, tone: 'bg-emerald-50 text-emerald-600' },
                { label: "Readiness 78", icon: BadgeCheck, tone: 'bg-amber-50 text-amber-600' }
            ].map(item => (
                <div key={item.label} className='flex flex-col items-center gap-1.5 rounded-2xl border border-slate-200 bg-white py-3'>
                    <span className={cn('grid size-10 place-items-center rounded-xl', item.tone)}>
                        <item.icon className='size-5' />
                    </span>

                    <span className='text-[11px] font-semibold text-slate-700'>{item.label}</span>
                </div>
            ))}
        </div>

        <div className='rounded-2xl border border-slate-200 bg-white p-4'>
            <div className='flex items-center justify-between'>
                <span className='text-[13px] font-bold'>Next up</span>
                <span className='text-[11px] font-semibold text-indigo-600'>Week 14</span>
            </div>

            <div className='mt-2 text-[12px] text-slate-600'>AI / ML Foundations · Fri, Lab 3</div>
        </div>
    </div>
)

const JourneyScreen: FC = (): ReactNode => (
    <div className='space-y-4'>
        <div className='rounded-[24px] bg-indigo-600 p-5 text-white shadow-[0_18px_36px_-18px_rgba(79,70,229,0.9)]'>
            <div className='flex items-center justify-between text-[11px] font-semibold text-indigo-100'>
                <span>Week 14 · Software</span>
                <span className='rounded-full bg-white/15 px-2 py-0.5'>Technical</span>
            </div>

            <div className='mt-3 text-[22px] font-bold leading-tight'>AI / ML Foundations</div>

            <div className='mt-2 flex items-center gap-3 text-[12px] text-indigo-100'>
                <span className='flex items-center gap-1'><Clock className='size-3.5' />Fri · 4:00 PM</span>
                <span className='flex items-center gap-1'><MapPin className='size-3.5' />Lab 3</span>
            </div>

            <div className='mt-4 grid grid-cols-3 gap-2 text-center'>
                {[['2', 'days'], ['06', 'hours'], ['41', 'mins']].map(([value, label]) => (
                    <div key={label} className='rounded-xl bg-white/10 py-2'>
                        <div className='font-mono text-[18px] font-bold'>{value}</div>
                        <div className='text-[10px] text-indigo-200'>{label}</div>
                    </div>
                ))}
            </div>

            <div className='mt-4 rounded-xl bg-white py-2.5 text-center text-[13px] font-bold text-indigo-700'>
                Register for this challenge
            </div>
        </div>

        <div className='rounded-2xl border border-slate-200 bg-white p-4'>
            <div className='flex items-center justify-between'>
                <div>
                    <div className='text-[11px] text-slate-500'>Level 7</div>
                    <div className='text-[18px] font-extrabold'>2,615 XP</div>
                </div>

                <span className='flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-bold text-orange-600'>
                    <Flame className='size-3.5' />
                    9-week streak
                </span>
            </div>

            <div className='mt-3 h-2 overflow-hidden rounded-full bg-slate-100'>
                <motion.div initial={{ width: 0 }} animate={{ width: '72%' }} transition={{ duration: 1 }} className='h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500' />
            </div>

            <div className='mt-1.5 text-[10.5px] text-slate-500'>385 XP to Level 8</div>
        </div>

        <div className='rounded-2xl border border-slate-200 bg-white p-4'>
            <div className='mb-2.5 text-[13px] font-bold'>Your 32-month path</div>

            <div className='flex gap-1'>
                {[['#6366F1', 100], ['#10B981', 45], ['#3B82F6', 0], ['#F59E0B', 0]].map(([color, done], index) => (
                    <div key={index} className='h-2 flex-1 overflow-hidden rounded-full bg-slate-100' style={{ flexGrow: [9, 11, 9, 3][index] }}>
                        <div className='h-full rounded-full' style={{ width: `${done}%`, backgroundColor: String(color) }} />
                    </div>
                ))}
            </div>

            <div className='mt-2 flex justify-between text-[10px] text-slate-500'>
                <span>Month 1</span>
                <span className='font-semibold text-emerald-600'>You are at Month 14</span>
                <span>Month 32</span>
            </div>
        </div>
    </div>
)

const VAULT_ITEMS = [
    { title: "Data Structures", meta: "PYQ · End Sem 2024", state: 'saved' },
    { title: "Digital Logic Design", meta: "PYQ · End Sem 2023", state: 'downloading' },
    { title: "Discrete Mathematics", meta: "Sessional II · 2024", state: 'idle' },
    { title: "OOP with C++", meta: "Lab Manual · 14 experiments", state: 'idle' },
    { title: "DSA Interview Sheet", meta: "Placement · 150 problems", state: 'idle' }
]

const VaultScreen: FC = (): ReactNode => (
    <div className='space-y-3'>
        <div className='flex gap-2 overflow-hidden'>
            {['Sem 3', 'CSE', 'PYQs'].map((chip, index) => (
                <span key={chip} className={cn('rounded-full px-3.5 py-1.5 text-[12px] font-semibold', index === 0 ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600')}>
                    {chip}
                </span>
            ))}
        </div>

        {VAULT_ITEMS.map((item, index) => (
            <motion.div
                key={item.title}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.06 }}
                className='rounded-2xl border border-slate-200 bg-white p-3.5'
            >
                <div className='flex items-center gap-3'>
                    <span className='grid size-11 shrink-0 place-items-center rounded-xl bg-rose-50 text-rose-600'>
                        <FileText className='size-5' />
                    </span>

                    <div className='min-w-0 flex-1'>
                        <div className='truncate text-[13.5px] font-semibold'>{item.title}</div>
                        <div className='text-[11px] text-slate-500'>{item.meta}</div>
                    </div>

                    <span className={cn('grid size-9 place-items-center rounded-full', item.state === 'saved' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600')}>
                        {item.state === 'saved' ? <Check className='size-4' /> : <Download className='size-4' />}
                    </span>
                </div>

                {item.state === 'downloading' && (
                    <div className='mt-3 h-1.5 overflow-hidden rounded-full bg-indigo-100'>
                        <motion.div initial={{ width: '8%' }} animate={{ width: '86%' }} transition={{ duration: 2.6, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' }} className='h-full rounded-full bg-indigo-500' />
                    </div>
                )}
            </motion.div>
        ))}
    </div>
)

const STEPS = [
    { label: "Applied", meta: "Mon", state: 'done' },
    { label: "Shortlisted", meta: "Thu", state: 'done' },
    { label: "Technical interview", meta: "Today · 2:30 PM", state: 'current' },
    { label: "Offer", meta: "Pending", state: 'todo' },
    { label: "Joined", meta: "Pending", state: 'todo' }
]

const ApplicationsScreen: FC = (): ReactNode => (
    <div className='space-y-4'>
        <div className='rounded-[24px] border border-slate-200 bg-white p-4'>
            <div className='flex items-center gap-3'>
                <span className='grid size-11 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 text-[13px] font-extrabold text-white'>
                    VR
                </span>

                <div>
                    <div className='text-[15px] font-bold'>Vertex Robotics</div>
                    <div className='text-[11.5px] text-slate-500'>Graduate Engineer Trainee</div>
                </div>
            </div>

            <div className='mt-4 space-y-0'>
                {STEPS.map((step, index) => (
                    <div key={step.label} className='relative flex gap-3 pb-4 last:pb-0'>
                        {index < STEPS.length - 1 && <span className={cn('absolute left-[13px] top-7 h-[calc(100%-20px)] w-0.5', step.state === 'done' ? 'bg-emerald-400' : 'bg-slate-200')} />}

                        <span className={cn('relative grid size-7 shrink-0 place-items-center rounded-full text-white', step.state === 'done' ? 'bg-emerald-500' : step.state === 'current' ? 'bg-indigo-600' : 'bg-slate-200')}>
                            {step.state === 'done' ? <Check className='size-3.5' /> : <span className='size-2 rounded-full bg-white' />}
                            {step.state === 'current' && <span className='absolute inset-0 animate-ping rounded-full bg-indigo-500/40' />}
                        </span>

                        <div>
                            <div className={cn('text-[13px] font-semibold', step.state === 'todo' && 'text-slate-400')}>{step.label}</div>
                            <div className={cn('text-[11px]', step.state === 'current' ? 'font-semibold text-indigo-600' : 'text-slate-500')}>{step.meta}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        <div className='flex items-center gap-4 rounded-2xl bg-slate-900 p-4 text-white'>
            <div className='relative grid size-16 place-items-center rounded-full bg-[conic-gradient(#10B981_0_78%,rgba(255,255,255,0.12)_78%_100%)]'>
                <span className='grid size-12 place-items-center rounded-full bg-slate-900 text-[16px] font-extrabold'>78</span>
            </div>

            <div>
                <div className='text-[13px] font-bold'>Readiness score</div>
                <div className='text-[11px] text-slate-400'>Shortlisted in the top 10% for this drive</div>
            </div>
        </div>
    </div>
)

const CheckInScreen: FC = (): ReactNode => {
    const [checkedIn, setCheckedIn] = useState(false)

    useEffect(() => {
        const timer = window.setInterval(() => setCheckedIn(value => !value), 2800)

        return () => window.clearInterval(timer)
    }, [])

    return (
        <div className='space-y-4'>
            <div className='rounded-2xl border border-slate-200 bg-white p-4'>
                <div className='text-[10.5px] font-bold uppercase tracking-[0.12em] text-indigo-600'>Tech Club · Workshop</div>
                <div className='mt-1 text-[16px] font-bold leading-snug'>Circuit Troubleshooting Workshop</div>

                <div className='mt-1.5 flex items-center gap-3 text-[11.5px] text-slate-500'>
                    <span className='flex items-center gap-1'><MapPin className='size-3.5' />Hall B</span>
                    <span className='flex items-center gap-1'><Clock className='size-3.5' />3:00 PM</span>
                </div>
            </div>

            <div className='relative mx-auto aspect-square w-full overflow-hidden rounded-[28px] bg-[#0B1628]'>
                <div className='absolute inset-10 grid place-items-center rounded-2xl bg-white/95 p-4 opacity-90'>
                    <QRCodeSVG value='GKV-CHECKIN-CIRCUIT-WORKSHOP' size={170} level='M' bgColor='#FFFFFF' fgColor='#0B1628' />
                </div>

                {['left-6 top-6 border-l-4 border-t-4 rounded-tl-2xl', 'right-6 top-6 border-r-4 border-t-4 rounded-tr-2xl', 'left-6 bottom-6 border-l-4 border-b-4 rounded-bl-2xl', 'right-6 bottom-6 border-r-4 border-b-4 rounded-br-2xl'].map(corner => (
                    <span key={corner} className={cn('absolute size-10 border-emerald-400', corner)} />
                ))}

                {!checkedIn && <span className='absolute inset-x-8 h-0.5 animate-scan rounded-full bg-emerald-400 shadow-[0_0_18px_4px_rgba(52,211,153,0.6)]' />}

                <AnimatePresence>
                    {checkedIn && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className='absolute inset-0 grid place-items-center bg-emerald-500/90 text-white'
                        >
                            <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 320, damping: 18 }} className='text-center'>
                                <span className='mx-auto grid size-16 place-items-center rounded-full bg-white text-emerald-600'>
                                    <Check className='size-8' strokeWidth={3} />
                                </span>

                                <div className='mt-3 text-[18px] font-bold'>Checked in</div>
                                <div className='text-[12px] text-emerald-50'>+40 GKV XP · attendance verified</div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <p className='text-center text-[11.5px] text-slate-500'>
                Certificate unlocks after the session ends
            </p>
        </div>
    )
}

const SCREENS: Record<ModuleId, FC> = {
    'student-hub': PassScreen,
    journey: JourneyScreen,
    'study-vault': VaultScreen,
    placements: ApplicationsScreen,
    marketplace: CheckInScreen
}

export const PhoneScreen: FC<PhoneScreenProps> = ({ active, onSelect }): ReactNode => {
    const Screen = SCREENS[active]

    return (
        <div className='flex size-full flex-col bg-[#F6F8FB] font-sans text-ink antialiased'>
            <div className='flex h-[50px] shrink-0 items-end justify-between px-7 pb-1.5 text-[14px] font-semibold'>
                <span>9:41</span>

                <span className='flex items-center gap-1.5'>
                    <Signal className='size-4' />
                    <Wifi className='size-4' />
                    <BatteryFull className='size-5' />
                </span>
            </div>

            <div className='flex h-[52px] shrink-0 items-center justify-between px-5'>
                <AnimatePresence mode='wait' initial={false}>
                    <motion.span
                        key={active}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className='text-[22px] font-extrabold tracking-tight'
                    >
                        {TITLES[active]}
                    </motion.span>
                </AnimatePresence>

                <span className='grid size-9 place-items-center rounded-full bg-slate-900 text-[12px] font-bold text-white'>
                    AS
                </span>
            </div>

            <div className='relative flex-1 overflow-hidden px-4 pt-2'>
                <AnimatePresence mode='wait' initial={false}>
                    <motion.div
                        key={active}
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -24 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Screen />
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className='shrink-0 border-t border-slate-200 bg-white/95 px-3 pb-6 pt-2'>
                <div className='flex justify-between'>
                    {TABS.map(tab => {
                        const selected = tab.id === active

                        return (
                            <button
                                key={tab.id}
                                type='button'
                                tabIndex={-1}
                                onClick={() => onSelect(tab.id)}
                                className={cn('flex w-16 flex-col items-center gap-1 rounded-xl py-1.5 text-[10.5px] font-semibold transition-colors', selected ? 'text-indigo-600' : 'text-slate-400')}
                            >
                                <tab.icon className='size-[22px]' strokeWidth={selected ? 2.4 : 2} />
                                {tab.label}
                            </button>
                        )
                    })}
                </div>

                <span className='mx-auto mt-2 block h-1.5 w-32 rounded-full bg-slate-900' />
            </div>
        </div>
    )
}
